import { useTranslation } from 'react-i18next';

import { send } from '@actual-app/core/platform/client/connection';
import type { FetchRatesResult } from '@actual-app/core/server/exchange-rates/app';
import type { ExchangeRateEntity } from '@actual-app/core/types/models';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { t } from 'i18next';
import { v4 as uuidv4 } from 'uuid';

import { addNotification } from '#notifications/notificationsSlice';
import { useDispatch } from '#redux';
import type { AppDispatch } from '#redux/store';

import { exchangeRateQueries } from './queries';

function invalidateQueries(queryClient: QueryClient) {
  void queryClient.invalidateQueries({
    queryKey: exchangeRateQueries.lists(),
  });
  // Converted balances are derived from the rates, so every sheet value
  // computed from transactions is stale now.
  void queryClient.invalidateQueries({ queryKey: ['transactions'] });
}

function dispatchErrorNotification(
  dispatch: AppDispatch,
  message: string,
  error?: Error,
) {
  dispatch(
    addNotification({
      notification: {
        id: uuidv4(),
        type: 'error',
        message,
        pre: error ? error.message : undefined,
      },
    }),
  );
}

type CreateExchangeRatePayload = Omit<ExchangeRateEntity, 'id' | 'tombstone'>;

export function useCreateExchangeRateMutation() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (rate: CreateExchangeRatePayload) => {
      return await send('exchange-rate-create', rate);
    },
    onSuccess: () => invalidateQueries(queryClient),
    onError: error => {
      console.error('Error creating exchange rate:', error);
      dispatchErrorNotification(
        dispatch,
        t('There was an error saving the exchange rate. Please try again.'),
        error,
      );
    },
  });
}

type UpdateExchangeRatePayload = Pick<ExchangeRateEntity, 'id'> &
  Partial<ExchangeRateEntity>;

export function useUpdateExchangeRateMutation() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (rate: UpdateExchangeRatePayload) => {
      return await send('exchange-rate-update', rate);
    },
    onSuccess: () => invalidateQueries(queryClient),
    onError: error => {
      console.error('Error updating exchange rate:', error);
      dispatchErrorNotification(
        dispatch,
        t('There was an error saving the exchange rate. Please try again.'),
        error,
      );
    },
  });
}

export function useDeleteExchangeRateMutation() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async ({ id }: Pick<ExchangeRateEntity, 'id'>) => {
      return await send('exchange-rate-delete', { id });
    },
    onSuccess: () => invalidateQueries(queryClient),
    onError: error => {
      console.error('Error deleting exchange rate:', error);
      dispatchErrorNotification(
        dispatch,
        t('There was an error deleting the exchange rate. Please try again.'),
        error,
      );
    },
  });
}

/** Human-readable text for an `exchange-rates-fetch` error. */
export function getFetchRatesError(
  result: Extract<FetchRatesResult, { ok: false }>,
): string {
  switch (result.error) {
    case 'unauthorized':
      return t('You are not logged in.');
    case 'no-server':
      return t('Connect to an Actual server to fetch exchange rates.');
    case 'no-budget-currency':
      return t('Choose a budget currency first.');
    case 'no-foreign-accounts':
      return t(
        'No account uses a currency other than the budget currency, so there is nothing to fetch.',
      );
    case 'NOT_CONFIGURED':
      return t('Open Exchange Rates has not been set up yet.');
    case 'INVALID_APP_ID':
      return t(
        'Open Exchange Rates rejected the App ID. Check it and try again.',
      );
    case 'RATE_LIMITED':
      return t(
        'Open Exchange Rates has restricted access for this App ID. The monthly quota may be used up.',
      );
    default:
      return result.reason
        ? t('Fetching exchange rates failed: {{reason}}', {
            reason: result.reason,
          })
        : t(
            'Fetching exchange rates failed. Please try again. (ref: {{error}})',
            {
              error: result.error,
            },
          );
  }
}

/**
 * Pull today's (or one day's) rates from the configured provider. Errors
 * are surfaced as notifications and the settled result is returned so the
 * caller can show a summary.
 */
export function useFetchExchangeRatesMutation() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async ({ date }: { date?: string } = {}) => {
      return await send('exchange-rates-fetch', date ? { date } : undefined);
    },
    onSuccess: result => {
      if (result.ok === false) {
        dispatchErrorNotification(dispatch, getFetchRatesError(result));
        return;
      }
      invalidateQueries(queryClient);
    },
    onError: error => {
      console.error('Error fetching exchange rates:', error);
      dispatchErrorNotification(
        dispatch,
        t('There was an error fetching exchange rates. Please try again.'),
        error,
      );
    },
  });
}
